import {
  ACCOUNT_EXPIRED,
  INVALID_USER_OR_TOKEN,
  IP_NOT_ALLOWLISTED,
  USERS_ACCOUNT_BLOCKED,
  RESET_PASSWORD_REQUIRED,
} from 'constants/apiErrorCodes'

import { BarError } from 'classes/errors'
import { EXPIRATION_STRATEGY } from 'app_modules/accounts/constants'
import { isDateExpired } from 'helpers'
import { isEmpty } from 'lodash'
import { notifyError, notifyWarning } from 'app_modules/notifications/actions'
import { PENDING_LIMITED_MEMBER, PENDING_PRO_MEMBER, PRE_ACCEPTED } from 'constants/invitationStatus'
import { resumeStatePersistence } from 'app_modules/localStorage/localStorage'
import { resetState } from 'app_modules/session/actions/resetState'
import * as invitesSelectors from 'app_modules/invites/selectors'
import actionTypes from 'app_modules/session/constants'
import api from 'api'
import { generateDpopKeyPair, createDpopProof } from 'utils/dpop'
import { saveDpopKeyPair } from 'utils/dpopKeyStore'

import { setUser } from './setUser'

const { API_URL } = process.env

// Actions

export const fetchLoginFailure = error => ({
  payload: error,
  type: actionTypes.FETCH_LOGIN_FAILURE,
})

export const fetchLoginRequest = () => ({
  type: actionTypes.FETCH_LOGIN_REQUEST,
})

export const fetchLoginSuccess = () => ({
  type: actionTypes.FETCH_LOGIN_SUCCESS,
})

// Thunks

export const login = (credentials, onError) => {
  const { token: invitationToken } = credentials

  const thunk = async (dispatch, getState) => {
    resumeStatePersistence()
    dispatch(resetState({ omit: ['invites'] }))
    dispatch(fetchLoginRequest())

    // Generate a fresh DPoP key pair before the login request so the proof can be
    // attached immediately. The private key is non-extractable. It is held in Redux and
    // also persisted to IndexedDB (still non-extractable) so a page reload reuses the
    // same key instead of presenting a new one the backend would reject (EEDTD-101).
    const loginEndpoint = invitationToken ? '/sessions/login_link_personal' : '/sessions/login'
    let keyPair = null
    let dpopProof = null
    try {
      keyPair = await generateDpopKeyPair()
      dpopProof = await createDpopProof(keyPair, 'POST', `${API_URL}${loginEndpoint}`)
    } catch {
      // WebCrypto unavailable or key/proof generation failed — proceed without DPoP.
      // The BE will reject with 643 in enforce mode; in soft mode login succeeds normally.
      keyPair = null
    }

    // Only persist the key pair when proof generation also succeeded: if generateDpopKeyPair
    // succeeded but createDpopProof threw, storing the key would cause all subsequent
    // authenticated requests to send proofs that the BE has no matching fingerprint for,
    // triggering 644 (DPOP_INVALID) and a forced logout.
    if (keyPair && dpopProof) {
      dispatch({ type: actionTypes.SET_DPOP_KEY_PAIR, payload: { dpopKeyPair: keyPair } })
      // Overwrites any key left from a previous session. Never rejects; if IndexedDB is
      // unavailable the session still works, it just cannot survive a reload.
      await saveDpopKeyPair(keyPair)
    }

    const credentialsWithDpop = dpopProof ? { ...credentials, dpopProof } : credentials

    const handleResponse = ({ user }) => {
      const { defaultAccount, profiles } = user

      const state = getState()

      const { account, status: invitationStatus } = invitesSelectors.invitation(state)

      const { id: invitationId } = account || {}

      const defaultProfile = profiles.find(p => p.account && p.account.id === defaultAccount?.id)
      const isExpired =
        !defaultProfile?.limitedMember &&
        defaultAccount?.expirationStrategy !== EXPIRATION_STRATEGY.DO_NOTHING &&
        defaultProfile?.license &&
        isDateExpired(defaultProfile.license.expiresAt)
      const isInvitationPending = invitationStatus === PENDING_PRO_MEMBER || invitationStatus === PENDING_LIMITED_MEMBER
      const isInvitationPreAccepted = invitationStatus === PRE_ACCEPTED
      let labelError = ''
      if (defaultAccount?.enableSso) {
        labelError = 'ssoEnabledAccountError'
      } else if (isEmpty(profiles)) {
        labelError = 'loginError'
      } else if (isExpired && !isInvitationPending && !isInvitationPreAccepted) {
        labelError = 'accountExpired'
      }

      if (!isEmpty(labelError)) {
        const error = new BarError({ label: labelError })
        dispatch(fetchLoginFailure(error))
        dispatch(notifyError(error))
        return onError()
      }

      const promises = [
        dispatch(setUser(user, isInvitationPending, isInvitationPreAccepted, invitationId)),
        dispatch(fetchLoginSuccess()),
      ]

      /* if (invitationStatus === PRE_ACCEPTED && !invitationToken) {
        promises.push(dispatch(acceptInvitation(true)));
      } */

      return Promise.all(promises)
    }

    const handleError = ({ error, isConnectionError }) => {
      let label = ''
      let notificationAction = ''

      switch (error.errorCode) {
        case INVALID_USER_OR_TOKEN:
          label = 'invalidEmailOrPassword'
          notificationAction = notifyError
          break
        case ACCOUNT_EXPIRED:
          label = 'accountExpired'
          notificationAction = notifyWarning
          break
        case USERS_ACCOUNT_BLOCKED:
          label = 'loginAccountBlocked'
          notificationAction = notifyWarning
          break
        case RESET_PASSWORD_REQUIRED: {
          const rawMsg = Array.isArray(error.message) ? error.message[0] : error.message
          label = rawMsg ? '' : 'resetPasswordRequired'
          notificationAction = notifyWarning
          dispatch(fetchLoginFailure(error))
          if (!isConnectionError) {
            dispatch(
              notificationAction(
                new BarError({
                  ...error,
                  label,
                  ...(rawMsg && { message: rawMsg }),
                }),
              ),
            )
          }
          onError()
          return
        }
        case IP_NOT_ALLOWLISTED:
          label = 'ipNotAllowlisted'
          notificationAction = notifyWarning
          break
        default:
          label = 'loginError'
          notificationAction = notifyError
      }

      dispatch(fetchLoginFailure(error))

      if (!isConnectionError) {
        dispatch(notificationAction(new BarError({ ...error, label })))
      }
      onError()
    }

    return invitationToken
      ? api.session.loginLinkRelationshipsProfile(credentialsWithDpop, handleResponse, handleError)
      : api.session.login(credentialsWithDpop, handleResponse, handleError)
  }

  thunk.type = actionTypes.FETCH_LOGIN

  return thunk
}

export default login
